import { LECTERN } from '@vrlearning/lesson-schema';
import type { ResolvedView } from '../engine/view.js';

/**
 * Where a pupil may teleport in Bekijken mode (ADR-058). In a staged world: the standing area's middle
 * (where the pupil starts, looking along −z) and a ring inside it (ADR-061: 2 m, its floor flat in
 * every staged world), each point looking outward. Round an object: four points at its reach and 1.5 m (at
 * least 2.5 m), the front one first, each looking at it. A teleport is the fade of docs/04. No point
 * sits behind the lectern's panel as the pupil sees it from the start (it could not be pressed): the
 * ring is turned half a step so none lies straight ahead, and an object's front point the panel hides
 * is where the pupil starts instead.
 */
export type TeleportPoint = {
  /** Where it is on the floor, x and z. */
  readonly at: readonly [number, number];
  /** Where the rig stands there (`Stage.placeRig`). */
  readonly view: ResolvedView;
};

export const WORLD_RING = { radius: 1.5, count: 4 } as const;
const EYE = 1.6;
const round = (n: number): number => Math.round(n * 100) / 100 + 0;

function pointAt(x: number, z: number, look: readonly [number, number, number]): TeleportPoint {
  return { at: [x, z], view: { position: [x, EYE, z], lookAt: look, floor: [x, 0, z] } };
}

/** Room left round the panel's edge, for its tilt and the ray's own thickness. */
const LECTERN_MARGIN = 0.1;

/**
 * Whether the lectern's panel, where docs/04 puts it before the pupil moves (1.9 m straight ahead,
 * its middle LECTERN.panelY above a point 0.95 m below the eyes), stands in the line of sight from
 * the start to this floor point. The panel is taken upright, with a margin for its tilt.
 */
export function hiddenByLectern(at: readonly [number, number]): boolean {
  const [x, z] = at;
  const ahead = -z;
  if (ahead <= LECTERN.distance) return false;
  // Where the line from the eyes to the point crosses the lectern's plane.
  const t = LECTERN.distance / ahead;
  const side = Math.abs(x * t);
  const height = EYE * (1 - t);
  const middle = Math.max(EYE - LECTERN.belowEye, LECTERN.minHeight) + LECTERN.panelY;
  return (
    side <= LECTERN.width / 2 + LECTERN_MARGIN &&
    Math.abs(height - middle) <= LECTERN.height / 2 + LECTERN_MARGIN
  );
}

export function worldPoints(): TeleportPoint[] {
  const points = [pointAt(0, 0, [0, EYE, -1])];
  for (let i = 0; i < WORLD_RING.count; i++) {
    // Half a step round: no point straight ahead, where the lectern stands.
    const angle = ((i + 0.5) / WORLD_RING.count) * Math.PI * 2;
    const x = round(Math.sin(angle) * WORLD_RING.radius);
    const z = round(-Math.cos(angle) * WORLD_RING.radius);
    points.push(pointAt(x, z, [x * 2, EYE, z * 2]));
  }
  return points.filter((point) => !hiddenByLectern(point.at));
}

export function objectPoints(item: {
  readonly pos: readonly [number, number, number];
  /** The furthest its footprint (its clips swept in) reaches from where it stands. */
  readonly reach: number;
}): TeleportPoint[] {
  const radius = Math.max(2.5, item.reach + 1.5);
  const [cx, , cz] = item.pos;
  const around: [number, number][] = [
    [0, 1],
    [1, 0],
    [0, -1],
    [-1, 0],
  ];
  return around.map(([dx, dz], index) => {
    const at: [number, number] = [round(cx + dx * radius), round(cz + dz * radius)];
    // The front point behind the panel: where the pupil starts, which looks at it from the front.
    // The one behind the object stays: the object hides it from the start anyway, and it is reached
    // from the points at its sides.
    const front = index === 0;
    return front && hiddenByLectern(at) ? pointAt(0, 0, [cx, 1, cz]) : pointAt(...at, [cx, 1, cz]);
  });
}
